import PropTypes from 'prop-types';
import {
  Avatar,
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import SecondaryButton from 'common/components/buttons/secondaryButton';
import GeneralGrid from 'common/components/layout/grid/GeneralGrid';
import SegmentedControl from 'common/components/navigation/segmentedControl';
import LoadingProgress from 'common/components/progress/loading';
import { BASE_S3 } from 'config/consts';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { appColors } from 'styles/colors';
import { getJourneyRanking } from './journeysActions';

const CARD_STYLE = {
  background: '#ffffff',
  borderRadius: 8,
  padding: 20,
};

const PODIUM_COLORS = { 1: '#E0B93C', 2: '#B9B7B2', 3: '#D08A63' };
const PODIUM_HEIGHTS = { 1: 88, 2: 64, 3: 48 };

const CRITERIA = [
  {
    key: 'fastest',
    label: 'Terminou primeiro',
    column: 'Tempo',
    description: 'Quem concluiu todas as atividades da jornada em menos tempo.',
    empty: 'Ninguém concluiu todas as atividades desta jornada ainda.',
  },
  {
    key: 'correct',
    label: 'Mais acertos',
    column: 'Acertos',
    description: 'Quem mais acertou perguntas nas atividades da jornada.',
    empty: 'Nenhum acerto registrado nas atividades desta jornada ainda.',
  },
  {
    key: 'points',
    label: 'Mais pontos',
    column: 'Pontos',
    description: 'Quem acumulou mais pontos nas atividades da jornada.',
    empty: 'Nenhum ponto distribuído nas atividades desta jornada ainda.',
  },
];

function formatDuration(seconds) {
  if (seconds < 60) {
    return `${seconds}s`;
  }
  if (seconds < 3600) {
    const rest = seconds % 60;
    return rest > 0
      ? `${Math.floor(seconds / 60)} min ${rest}s`
      : `${Math.floor(seconds / 60)} min`;
  }
  if (seconds < 86400) {
    const minutes = Math.floor((seconds % 3600) / 60);
    return minutes > 0
      ? `${Math.floor(seconds / 3600)}h ${minutes}min`
      : `${Math.floor(seconds / 3600)}h`;
  }
  const hours = Math.floor((seconds % 86400) / 3600);
  return hours > 0
    ? `${Math.floor(seconds / 86400)}d ${hours}h`
    : `${Math.floor(seconds / 86400)}d`;
}

function formatValue(criterionKey, value) {
  if (value === null || value === undefined) {
    return '-';
  }
  if (criterionKey === 'fastest') {
    return formatDuration(value);
  }
  if (criterionKey === 'correct') {
    return value === 1 ? '1 acerto' : `${value} acertos`;
  }
  return `${Number.isInteger(value) ? value : value.toFixed(2).replace('.', ',')} pts`;
}

function shortName(name) {
  const parts = `${name || ''}`.trim().split(' ').filter(Boolean);
  if (parts.length < 2) {
    return parts[0] || '';
  }
  return `${parts[0]} ${parts[parts.length - 1][0]}.`;
}

function initials(name) {
  const parts = `${name || ''}`.trim().split(' ').filter(Boolean);
  if (parts.length < 1) {
    return '?';
  }
  const last = parts.length > 1 ? parts[parts.length - 1] : '';
  return `${parts[0][0]}${last ? last[0] : ''}`.toUpperCase();
}

function ParticipantAvatar({ item, size, background }) {
  return (
    <Avatar
      alt={item.name}
      src={item.image ? `${BASE_S3}/${item.image}` : undefined}
      style={{
        width: size,
        height: size,
        background,
        color: '#fff',
        fontSize: size / 2.6,
        fontWeight: 'bold',
      }}>
      {initials(item.name)}
    </Avatar>
  );
}

ParticipantAvatar.propTypes = {
  item: PropTypes.shape({ name: PropTypes.string, image: PropTypes.string }),
  size: PropTypes.number,
  background: PropTypes.string,
};

function Podium({ top, criterionKey }) {
  const order =
    top.length > 2 ? [top[1], top[0], top[2]] : top.length === 2 ? [top[1], top[0]] : [top[0]];

  return (
    <Box
      display="flex"
      alignItems="flex-end"
      justifyContent="center"
      style={{ gap: 16, margin: '8px auto 32px auto', maxWidth: 480 }}>
      {order.map((item, index) => {
        if (!item) {
          return <Box key={`empty-${index}`} style={{ flex: 1 }} />;
        }
        const color = PODIUM_COLORS[item.position];
        return (
          <Box
            key={item.user_id}
            display="flex"
            flexDirection="column"
            alignItems="center"
            style={{ flex: 1, maxWidth: '33%' }}>
            <ParticipantAvatar
              item={item}
              size={item.position === 1 ? 64 : 52}
              background={color}
            />
            <Typography
              variant="body2"
              align="center"
              title={item.name}
              noWrap
              style={{ fontWeight: 500, marginTop: 8, maxWidth: '100%' }}>
              {shortName(item.name)}
            </Typography>
            <Typography
              variant="caption"
              align="center"
              style={{ color: appColors.dark.level4, marginBottom: 8, minHeight: 20 }}>
              {formatValue(criterionKey, item.value)}
            </Typography>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              style={{
                width: '100%',
                height: PODIUM_HEIGHTS[item.position],
                background: color,
                borderRadius: '6px 6px 0 0',
              }}>
              <Typography variant="h6" style={{ color: '#fff', fontWeight: 'bold' }}>
                {item.position}
              </Typography>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

Podium.propTypes = {
  top: PropTypes.array,
  criterionKey: PropTypes.string,
};

function JourneyRanking(props) {
  const { journeys, router, initialJourneyId } = props;
  const list = journeys.list;
  const ranking = journeys.ranking;
  const fromUrl = list.find((item) => item.id === initialJourneyId);
  const [journeyId, setJourneyId] = useState(
    fromUrl ? fromUrl.id : list.length > 0 ? list[0].id : undefined
  );
  const [criterionKey, setCriterionKey] = useState(CRITERIA[0].key);

  useEffect(() => {
    if (journeyId) {
      props.getJourneyRanking(journeyId);
    }
    // eslint-disable-next-line
  }, [journeyId]);

  const criterion = CRITERIA.find((item) => item.key === criterionKey);
  const data = ranking?.rankings?.[criterionKey];

  function renderTable() {
    if (journeys.rankingLoading) {
      return (
        <Box display="flex" justifyContent="center" style={{ padding: 40 }}>
          <LoadingProgress />
        </Box>
      );
    }

    if (journeys.rankingError) {
      return (
        <Box style={{ padding: 20 }}>
          <Typography variant="body2" style={{ color: appColors.dark.level3 }}>
            Não foi possível carregar o rank desta jornada.
          </Typography>
          <SecondaryButton onClick={() => props.getJourneyRanking(journeyId)}>
            Tentar de novo
          </SecondaryButton>
        </Box>
      );
    }

    if (!data || data.top.length < 1) {
      return (
        <Typography variant="body2" style={{ color: appColors.dark.level3, padding: '20px 0' }}>
          {criterion.empty}
        </Typography>
      );
    }

    return (
      <>
        <Podium top={data.top} criterionKey={criterionKey} />
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell align="center" style={{ width: 80 }}>
                Posição
              </TableCell>
              <TableCell>Participante</TableCell>
              <TableCell align="center">Atividades concluídas</TableCell>
              <TableCell align="center">{criterion.column}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.top.map((item) => (
              <TableRow key={item.user_id}>
                <TableCell align="center">
                  <Typography
                    variant="body2"
                    style={{
                      fontWeight: item.position < 4 ? 'bold' : 'normal',
                      color: PODIUM_COLORS[item.position] || appColors.dark.level2,
                    }}>
                    {item.position}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center">
                    <Box style={{ marginRight: 12 }}>
                      <ParticipantAvatar
                        item={item}
                        size={32}
                        background={appColors.v2.mediumGray}
                      />
                    </Box>
                    {item.name}
                  </Box>
                </TableCell>
                <TableCell align="center">
                  {item.completed}
                  {ranking.total_actions ? ` de ${ranking.total_actions}` : ''}
                </TableCell>
                <TableCell align="center">{formatValue(criterionKey, item.value)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {data.total_participants > data.top.length ? (
          <Typography
            variant="caption"
            style={{ color: appColors.dark.level4, display: 'block', marginTop: 12 }}>
            Mostrando as {data.top.length} primeiras posições de {data.total_participants}{' '}
            participantes neste rank.
          </Typography>
        ) : null}
      </>
    );
  }

  return (
    <GeneralGrid container spacing={3}>
      <GeneralGrid item xs={12}>
        <Box style={CARD_STYLE}>
          <GeneralGrid container spacing={3} alignItems="center">
            <GeneralGrid item xs={12} md={5}>
              <FormControl fullWidth>
                <InputLabel id="journey-rank-select">Jornada</InputLabel>
                <Select
                  labelId="journey-rank-select"
                  value={journeyId || ''}
                  onChange={(event) => setJourneyId(event.target.value)}>
                  {list.map((journey) => (
                    <MenuItem key={journey.id} value={journey.id}>
                      {journey.name || `Jornada ${journey.id}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </GeneralGrid>
            <GeneralGrid item xs={12} md={7}>
              <Box display="flex" justifyContent="flex-end" alignItems="center">
                <SegmentedControl
                  options={CRITERIA.map((item) => ({ value: item.key, label: item.label }))}
                  value={criterionKey}
                  onChange={setCriterionKey}
                />
              </Box>
            </GeneralGrid>
          </GeneralGrid>

          <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
            <Typography variant="body2" style={{ color: appColors.dark.level3 }}>
              {criterion.description}
            </Typography>
            {journeyId ? (
              <SecondaryButton
                onClick={() =>
                  router.push(`/owner/journeys/journey/${journeyId}/relatorio?from=rank`)
                }>
                Ver relatório
              </SecondaryButton>
            ) : null}
          </Box>

          {renderTable()}
        </Box>
      </GeneralGrid>
    </GeneralGrid>
  );
}

JourneyRanking.propTypes = {
  getJourneyRanking: PropTypes.func,
  initialJourneyId: PropTypes.number,
  journeys: PropTypes.shape({
    list: PropTypes.array,
    ranking: PropTypes.object,
    rankingLoading: PropTypes.bool,
    rankingError: PropTypes.bool,
  }),
  router: PropTypes.shape({ push: PropTypes.func }),
};

const mapStateToProps = (state) => {
  return { journeys: state.journeys, auth: state.auth };
};

const mapDispatchToProps = (dispatch) => {
  return bindActionCreators({ getJourneyRanking }, dispatch);
};

export default connect(mapStateToProps, mapDispatchToProps)(JourneyRanking);
